/**
 * Notification Service
 *
 * API client for notification operations
 */

import { get, post } from '@/lib/api'
import { API_ROUTES } from '@/config/constants'
import type {
  NotificationsResponse,
  UnreadCountResponse,
  MarkAsReadResponse,
  MarkAllAsReadResponse,
  DeleteNotificationResponse,
  NotificationFilters,
} from '@/domain/notification'

// =============================================================================
// API Functions
// =============================================================================

/**
 * Get notifications for the current user
 */
export async function getNotifications(
  filters?: NotificationFilters
): Promise<NotificationsResponse> {
  const params = new URLSearchParams()

  if (filters?.isRead !== undefined) {
    params.append('isRead', filters.isRead.toString())
  }
  if (filters?.limit) {
    params.append('limit', filters.limit.toString())
  }
  if (filters?.offset) {
    params.append('offset', filters.offset.toString())
  }

  const queryString = params.toString()
  const url = queryString
    ? `${API_ROUTES.NOTIFICATIONS.BASE}?${queryString}`
    : API_ROUTES.NOTIFICATIONS.BASE

  return get<NotificationsResponse>(url)
}

/**
 * Get unread notification count
 */
export async function getUnreadCount(): Promise<UnreadCountResponse> {
  return get<UnreadCountResponse>(API_ROUTES.NOTIFICATIONS.UNREAD_COUNT)
}

/**
 * Mark a notification as read
 */
export async function markAsRead(notificationId: string): Promise<MarkAsReadResponse> {
  return post<MarkAsReadResponse>(API_ROUTES.NOTIFICATIONS.MARK_READ(notificationId), {})
}

/**
 * Mark all notifications as read
 */
export async function markAllAsRead(): Promise<MarkAllAsReadResponse> {
  return post<MarkAllAsReadResponse>(API_ROUTES.NOTIFICATIONS.MARK_ALL_READ, {})
}

/**
 * Delete a notification
 */
export async function deleteNotification(
  notificationId: string
): Promise<DeleteNotificationResponse> {
  return post<DeleteNotificationResponse>(
    API_ROUTES.NOTIFICATIONS.DELETE(notificationId),
    {}
  )
}

/**
 * Delete all notifications
 */
export async function deleteAllNotifications(): Promise<{ message: string; count: number }> {
  return post<{ message: string; count: number }>(
    API_ROUTES.NOTIFICATIONS.DELETE_ALL,
    {}
  )
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Format notification timestamp for display
 */
export function formatNotificationTime(timestamp: string): string {
  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) {
    return 'Ahora'
  }
  if (diffMins < 60) {
    return `Hace ${diffMins}m`
  }
  if (diffHours < 24) {
    return `Hace ${diffHours}h`
  }
  if (diffDays < 7) {
    return `Hace ${diffDays}d`
  }

  return date.toLocaleDateString('es-CO', {
    month: 'short',
    day: 'numeric',
  })
}
